import { Fragment, useId, type CSSProperties } from 'react'
import type { TimelineMessage } from '@/shared/api'
import { cn } from '@/shared/lib/cn'
import { Icon, Tooltip } from '@/shared/ui'
import { channelKindToken } from '../model/channel-kind'
import { deliveryMark } from '../model/timeline'
import s from './MessageBubble.module.css'

/** Действие под несостоявшимся сообщением; `hint` — подсказка к нему. */
export type FailedAction = { label: string; hint?: string; run: () => void }

type MessageBubbleProps = {
  message: TimelineMessage
  /**
   * Действия под несостоявшимся сообщением. Приходят СНАРУЖИ — и только от очереди отправки
   * (`features/message-send`), которая одна знает ключ идемпотентности этого сообщения.
   *
   * **У сообщения из ЛЕНТЫ их нет, и это не упущение.** Сервер отдаёт `deliveryStatus: 'failed'` и
   * у чужих сообщений — тех, что уходили не из этой вкладки. Ключа идемпотентности у них взять
   * неоткуда: он живёт в очереди отправителя. Повтор с НОВЫМ ключом сервер не узнает (выполненное
   * действие он помнит по ключу, `IdempotencyKey`), и если гостю всё-таки ушло, он получит сообщение
   * дважды. Поэтому там — только «Не отправлено».
   */
  actions?: ReadonlyArray<FailedAction>
}

/**
 * Пузырь сообщения мультиканальной ленты брони (обновлённый макет чата). Входящий — слева,
 * `--panel` с обводкой; исходящий — справа, `--accent`. Асимметричный радиус вместо хвоста.
 *
 * Новое против прежней ленты: **тег отправителя** (кто на нашей стороне — менеджер или «ИИ»),
 * **бейдж канала** (лента брони собирает WhatsApp, бота, SMS и веб вместе — у каждого сообщения
 * свой род, цвет — `channelKindToken`), **вложение** (рисуем строго то, что прислал сервер —
 * фейковых файлов не выдумываем) и **строка статуса** (`statusLine`) у `failed`.
 *
 * Время и галочки — ОДНА строка текста (`13:52 ✓✓`), так в макете; карту статус → галочка держит
 * `model/timeline`. `sending` — приглушённый пузырь; `failed` — строка «Не отправлено» СНАРУЖИ
 * пузыря (красный на `--accent` нечитаем — 1.05 : 1, измерено; на `--panel` берёт AA).
 */
export function MessageBubble({ message, actions = [] }: MessageBubbleProps) {
  const hintId = useId()
  const outgoing = message.direction === 'out'
  const mark = deliveryMark(message.deliveryStatus)
  const failed = message.deliveryStatus === 'failed'

  return (
    <>
      <div
        className={cn(
          s.bubble,
          outgoing ? s.outgoing : s.incoming,
          message.deliveryStatus === 'sending' && s.sending,
        )}
      >
        {message.sender ? <span className={s.sender}>{message.sender.label}</span> : null}

        {message.text ? <span className={s.text}>{message.text}</span> : null}

        {message.attachment ? <AttachmentChip attachment={message.attachment} /> : null}

        <span className={s.meta}>
          {message.channel ? (
            <span className={s.channel}>
              <span
                className={s.channelDot}
                style={{ '--ch': channelKindToken(message.channel.kind) } as CSSProperties}
                aria-hidden="true"
              />
              {message.channel.label}
            </span>
          ) : null}
          <span className={s.time}>
            {mark ? `${message.timeLabel} ${mark}` : message.timeLabel}
          </span>
        </span>
      </div>

      {failed ? (
        // `data-failed-line` — по нему очередь отправки проверяет, видна ли строка.
        <div className={s.failed} data-failed-line="">
          <span className={s.failedDot} aria-hidden="true" />
          {message.statusLine ?? 'Не отправлено'}
          {actions.map((action, index) => {
            const described = action.hint ? `${hintId}-${index}` : undefined
            const button = (
              <button
                type="button"
                className={s.action}
                aria-describedby={described}
                onClick={action.run}
              >
                {action.label}
              </button>
            )
            // Подсказка кита — глазам, и по фокусу с клавиатуры тоже.
            return (
              <Fragment key={action.label}>
                <span aria-hidden="true">·</span>
                {action.hint ? <Tooltip content={action.hint}>{button}</Tooltip> : button}
              </Fragment>
            )
          })}
        </div>
      ) : null}

      {/* Читалке — постоянное описание (Radix вешает своё, лишь пока подсказка открыта). `hidden`:
          описание берётся и из скрытого узла, а обзор не прочтёт его второй раз. */}
      {failed
        ? actions.map((action, index) =>
            action.hint ? (
              <span key={action.label} id={`${hintId}-${index}`} hidden>
                {action.hint}
              </span>
            ) : null,
          )
        : null}
    </>
  )
}

/** Вложение: скачиваемое (`<a>`), если сервер дал `url`, иначе — просто плитка без действия. */
function AttachmentChip({ attachment }: { attachment: NonNullable<TimelineMessage['attachment']> }) {
  const body = (
    <>
      <span className={s.attachIcon} aria-hidden="true">
        <Icon name="file" size={15} strokeWidth={1.8} />
      </span>
      <span className={s.attachBody}>
        <span className={s.attachName}>{attachment.name}</span>
        <span className={s.attachSize}>{attachment.sizeLabel}</span>
      </span>
    </>
  )

  if (attachment.url) {
    return (
      <a className={s.attach} href={attachment.url} target="_blank" rel="noopener noreferrer">
        {body}
      </a>
    )
  }

  return <span className={s.attach}>{body}</span>
}
